/* Cross-client "My Work" inbox data model (Task-Management Phase 1 / T2).
 *
 * Backend surface (service/app/routers/workspace.py, session-scoped to the
 * signed-in user — decision D10): GET /me/tasks -> {tasks, total}, the
 * cross-client projection of the ONE shared needs_you predicate (plan §1.5).
 * Each row carries a server-resolved {client_slug, task_id} plus its standing
 * relation (`reason`) and attention flags — the board's per-client "Needs you"
 * pin reads the SAME predicate, so an AM sees one consistent attention set.
 *
 * This is the DATA half (fetch + honest total); rendering lives in
 * portfolio.ts, mirroring the notifications.ts / notifBell.ts split. Kept
 * dependency-light (only api/client.ts's request) so it is unit-testable under
 * Vitest's node env with a stubbed fetch, exactly like lib/notifications.ts.
 */
import { request } from '../api/client.ts';

export interface MyWorkFlags {
  regression: boolean;
  overdue: boolean;
  stalled: boolean;
  due: boolean;
  mention: boolean;
}

export type MyWorkReason = 'assigned' | 'watched' | 'mentioned';

export interface MyWorkRow {
  task_id: number;
  client_slug: string;
  brand: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  status_changed_at?: string | null;
  reason: MyWorkReason;
  flags: MyWorkFlags;
  hot: boolean;
  warm: boolean;
}

export interface MyWorkResponse {
  tasks: MyWorkRow[];
  total: number;
}

/** Pull the current My Work inbox. The server already orders it hot→warm→plain
 * (stall/overdue-first) and returns an honest, untruncated `total`, so the UI
 * renders the rows in the received order and trusts the count. */
export async function fetchMyWork(): Promise<MyWorkResponse> {
  const r = await request<MyWorkResponse>('/me/tasks');
  return { tasks: r.tasks || [], total: r.total ?? (r.tasks || []).length };
}

/** Badge tone drives COLOR, independent of the grouping TIER (rowTier). Red
 * (`hot`) is reserved for the sharpest adverse states — regressed / overdue —
 * per the pill-archetype "one red" budget (design/specs/pill-archetypes.md §4).
 * A stalled row is amber (`warm`), not red: pill-spec addendum D7 rules "stalled
 * = amber everywhere", matching the board's `.wsage.hot` indicator. An @mention
 * is a collaboration ping, not a regression, so it carries its own non-red
 * `mention` tone (azure, mirroring the `.mention` chip) while still ranking in
 * the hot tier. `warm` is amber (stalled / due-soon). */
export type BadgeTone = 'hot' | 'warm' | 'mention';

/** The single highest-priority attention label for a row, or null for a plain
 * standing relation — drives the per-row badge. Ranked exactly as the server
 * orders the hot tier (regression → overdue → stalled → mention), then the warm
 * `due`. Mention is hot-TIER but azure-TONED (non-red). Pure, so the badge
 * logic is unit-testable. */
export function rowBadge(row: MyWorkRow): { label: string; tone: BadgeTone } | null {
  const f = row.flags;
  if (f.regression) return { label: 'Regressed', tone: 'hot' };
  if (f.overdue) return { label: 'Overdue', tone: 'hot' };
  if (f.stalled) return { label: 'Stalled', tone: 'warm' };
  if (f.mention) return { label: 'Mention', tone: 'mention' };
  if (f.due) return { label: 'Due soon', tone: 'warm' };
  return null;
}

/** Tier for grouping/headers: hot | warm | plain (matches the server order). */
export function rowTier(row: MyWorkRow): 'hot' | 'warm' | 'plain' {
  return row.hot ? 'hot' : row.warm ? 'warm' : 'plain';
}

/** Partition rows into the three urgency tiers, preserving the server's
 * within-tier order (hot→warm→plain, stall/overdue-first). The inbox renders
 * hot first, so an AM opening the page sees fires before routine work. Pure —
 * unit-tested; portfolio.ts renders it. */
export function groupByTier(rows: MyWorkRow[]): Record<'hot' | 'warm' | 'plain', MyWorkRow[]> {
  const groups: Record<'hot' | 'warm' | 'plain', MyWorkRow[]> = { hot: [], warm: [], plain: [] };
  for (const r of rows) groups[rowTier(r)].push(r);
  return groups;
}

/* ── CRM My Work screen (#/work): STATE-ordered lanes ──────────────────────
 *
 * The CRM inbox (views/work.ts) orders by STATE, not by the hot/warm urgency
 * tier the legacy portfolio.ts panel uses — per the owner-approved export (S4,
 * crm-hub/my-work.html) and the build brief: "ordered by STATE — overdue,
 * waiting, ready to verify — not by a priority the system guessed for you".
 * Every function here is PURE (the core ordering logic the unit suite pins);
 * the fields it reads all come from the SAME /me/tasks projection above — this
 * is a VIEW of the one ledger, never a second store or a second derivation. */

/** The four state lanes, top-to-bottom. `overdue` is the sharpest adverse
 * state (past its connected due date); `waiting` is "an external actor owns the
 * next move" — kept visibly distinct from "not done yet"; `ready` is the
 * evidence-gated implemented state a probe can confirm; `open` is everything
 * else in flight. */
export type WorkLane = 'overdue' | 'waiting' | 'ready' | 'open';

export const LANE_ORDER: readonly WorkLane[] = ['overdue', 'waiting', 'ready', 'open'];

export interface LaneMeta {
  key: WorkLane;
  title: string;
  /** the worklane-head modifier class (crm.css): '' | 'critical' | 'waiting' | 'verified'. */
  headClass: '' | 'critical' | 'waiting' | 'verified';
  blurb: string;
}

export const LANE_META: Record<WorkLane, LaneMeta> = {
  overdue: {
    key: 'overdue', title: 'Overdue', headClass: 'critical',
    blurb: 'past its connected due date — needs action now',
  },
  waiting: {
    key: 'waiting', title: 'Waiting on an external actor', headClass: 'waiting',
    blurb: 'submitted — an editor, platform or client owns the next move; a probe can poll it, not resolve it',
  },
  ready: {
    key: 'ready', title: 'Ready to verify', headClass: 'verified',
    blurb: 'implemented — a deterministic recheck can confirm it (observed / not-observed / unavailable, never a click)',
  },
  open: {
    key: 'open', title: 'Open', headClass: '',
    blurb: 'assigned or watched work in flight',
  },
};

const WAITING_STATES = new Set(['waiting-client', 'waiting-platform']);

/** The state lane a row belongs to. Precedence (sharpest first): a past-due row
 * is `overdue` regardless of its status (the due date is the sharper signal —
 * even a waiting task that has blown its date needs escalating); then
 * waiting-on-external; then the implemented "ready to verify" state; else
 * `open`. Derived from STATE (status + the server's overdue flag), never an
 * invented priority score. Pure — the core ordering logic, unit-tested. */
export function workLane(row: MyWorkRow): WorkLane {
  if (row.flags.overdue) return 'overdue';
  if (WAITING_STATES.has(row.status)) return 'waiting';
  if (row.status === 'implemented') return 'ready';
  return 'open';
}

/** Partition rows into the state lanes, preserving the server's within-lane
 * order (hot→warm→plain, oldest-neglect first) via a stable partition — so a
 * regressed/stalled row still sorts to the top of its lane. Pure — unit-tested;
 * views/work.ts renders it in LANE_ORDER. */
export function groupByLane(rows: MyWorkRow[]): Record<WorkLane, MyWorkRow[]> {
  const g: Record<WorkLane, MyWorkRow[]> = { overdue: [], waiting: [], ready: [], open: [] };
  for (const r of rows) g[workLane(r)].push(r);
  return g;
}

/** Per-lane counts (honest — a lane with 0 rows renders as an explicit empty
 * lane, never hidden). */
export function laneCounts(rows: MyWorkRow[]): Record<WorkLane, number> {
  const g = groupByLane(rows);
  return { overdue: g.overdue.length, waiting: g.waiting.length, ready: g.ready.length, open: g.open.length };
}

/** The rail badge / "Open · assigned to you" count: tasks ASSIGNED to the user
 * (the `assigned` standing relation is open-by-construction on the server — R1
 * requires an open status). Watched / mentioned rows are attention the user
 * carries, not their own open work, so they never inflate this count. Pure. */
export function openAssignedCount(rows: MyWorkRow[]): number {
  return rows.filter((r) => r.reason === 'assigned').length;
}

/** ISO status → a human label for the neutral state pill / meta line. */
export function humanStatus(status: string): string {
  switch (status) {
    case 'in-progress': return 'In progress';
    case 'waiting-client': return 'Waiting on client';
    case 'waiting-platform': return 'Waiting on platform';
    case 'implemented': return 'Implemented';
    case 'regressed': return 'Regressed';
    case 'wont-do': return "Won't do";
    case 'open': return 'Open';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export type StateCls = 'attention' | 'waiting' | 'calm' | 'priority' | 'mention' | 'plain';

/** The most salient state for a row's `.status-label` pill, resolved from STATE
 * (status + flags), sharpest-first: regressed → overdue → waiting-on-external
 * (client/platform, kept distinct from "not done") → ready-to-verify → stalled
 * → @mention → high-priority → the plain humanized status. Pure — unit-tested;
 * views/work.ts maps `cls` to a token-only `.status-label` variant. */
export function workRowState(row: MyWorkRow): { label: string; cls: StateCls } {
  const f = row.flags;
  if (f.regression) return { label: 'Regressed', cls: 'attention' };
  if (f.overdue) return { label: 'Overdue', cls: 'attention' };
  if (row.status === 'waiting-client') return { label: 'Client', cls: 'waiting' };
  if (row.status === 'waiting-platform') return { label: 'Platform', cls: 'waiting' };
  if (row.status === 'implemented') return { label: 'Ready to verify', cls: 'calm' };
  if (f.stalled) return { label: 'Stalled', cls: 'waiting' };
  if (f.mention) return { label: 'Mention', cls: 'mention' };
  if (row.priority === 'high') return { label: 'High', cls: 'priority' };
  return { label: humanStatus(row.status), cls: 'plain' };
}
